import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { HandlebarsBlockType } from '../../types';
import { HandlebarsBlockEndView } from './HandlebarsBlockEndView';

export interface HandlebarsBlockEndOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    handlebarsBlockEnd: {
      insertHandlebarsBlockEnd: (attrs: {
        blockType: HandlebarsBlockType;
        blockId: string;
        description?: string | null;
        isValid?: boolean;
      }) => ReturnType;
    };
  }
}

export const HandlebarsBlockEnd = Node.create<HandlebarsBlockEndOptions>({
  name: 'handlebarsBlockEnd',

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false, // Prevent orphaning by disabling drag

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      blockType: {
        default: 'if',
        parseHTML: (element) =>
          (element.getAttribute('data-block-type') as HandlebarsBlockType) || 'if',
        renderHTML: (attributes) => ({
          'data-block-type': attributes.blockType,
        }),
      },
      blockId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-block-id') || '',
        renderHTML: (attributes) => ({
          'data-block-id': attributes.blockId,
        }),
      },
      description: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-description') || null,
        renderHTML: (attributes) => {
          if (!attributes.description) return {};
          return { 'data-description': attributes.description };
        },
      },
      isValid: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-valid') !== 'false',
        renderHTML: (attributes) => ({
          'data-valid': attributes.isValid ? 'true' : 'false',
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-handlebars-block-end]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const blockType = HTMLAttributes.blockType || 'if';
    return [
      'span',
      mergeAttributes(
        { 'data-handlebars-block-end': '' },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      `{{/${blockType}}}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HandlebarsBlockEndView, {
      className: 'handlebars-block-end-wrapper',
    });
  },

  addCommands() {
    return {
      insertHandlebarsBlockEnd:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              blockType: attrs.blockType,
              blockId: attrs.blockId,
              description: attrs.description ?? null,
              isValid: attrs.isValid ?? true,
            },
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Backspace handling - similar to block start
      Backspace: () =>
        this.editor.commands.command(({ state }) => {
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          let isBlockEnd = false;
          state.doc.nodesBetween(anchor - 1, anchor, (node) => {
            if (node.type.name === this.name) {
              isBlockEnd = true;
              return false;
            }
          });

          // Return true to prevent default, the view will handle the delete confirmation
          return isBlockEnd;
        }),
    };
  },
});

export default HandlebarsBlockEnd;
